import React, { useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";
import { WordCloudController, WordElement } from "chartjs-chart-wordcloud";

Chart.register(...registerables, WordCloudController, WordElement);

const WordCloudDisplay = ({
    wordFrequencies,
    width = 500,
    height = 400,
    minFontSize = 12,
    maxFontSize = 50,
}) => {
    const canvasRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
    
        if (canvas) {
            // Set canvas dimensions
            canvas.width = width;
            canvas.height = height;
    
            // Destroy existing chart instance if it exists
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
            }
    
    
            // Override size computation in WordElement.defaults
            WordElement.defaults.size = (ctx) => {
                const value = ctx.raw; // Word frequency
                const minFreq = Math.min(...wordFrequencies.map((word) => word.value));
                const maxFreq = Math.max(...wordFrequencies.map((word) => word.value));
                const canvasArea = width * height;
                const maxFontSizeScaled = Math.sqrt(canvasArea) / 10; // Scale font size based on canvas area
                const range = Math.min(maxFontSize, maxFontSizeScaled) - minFontSize;
            
                if (maxFreq === minFreq) {
                    return minFontSize; // If all words have the same frequency
                }
            
                // Scale font size based on frequency and canvas size
                return minFontSize + ((value - minFreq) / (maxFreq - minFreq)) * range;
            };
            
            
            // Configure the word cloud chart
            chartInstanceRef.current = new Chart(canvas, {
                type: "wordCloud",
                data: {
                    labels: wordFrequencies.map((word) => word.text),
                    datasets: [
                        {
                            label: "Word Cloud",
                            data: wordFrequencies.map((word) => word.value),
                        },
                    ],
                },
                options: {
                    responsive: false,
                    maintainAspectRatio: false,
                    layout: {
                        padding: 10,
                    },
                    plugins: {
                        legend: {
                            display: false,
                        },
                        tooltip: {
                            callbacks: {
                                label: (context) =>
                                    `${context.raw}: ${context.dataset.data[context.dataIndex]}`,
                            },
                        },
                    },
                    elements: {
                        word: {
                            fontFamily: "Arial",
                            fontColor: () => {
                                const colors = [
                                    "#FF6384",
                                    "#36A2EB",
                                    "#FFCE56",
                                    "#4BC0C0",
                                    "#9966FF",
                                ];
                                return colors[Math.floor(Math.random() * colors.length)];
                            },
                            rotate: 0, // Ensure horizontal layout
                            minRotation: 0,
                            maxRotation: 0,
                            rotationSteps: 1,
                            padding: 2, // Padding around words
                        },
                    },
                    datasets: {
                        fit: true,
                        autoGrow: {
                            maxTries: 3,
                            scalingFactor: 1.1, // Adjust scaling factor to ensure fit
                        },
                    },
                    
                },
            });            
        }
    
        // Cleanup on component unmount
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.destroy();
                chartInstanceRef.current = null;
            }
        };
    }, [wordFrequencies, width, height, minFontSize, maxFontSize]);    

    return (
        <div
            style={{
                width: `${width}px`,
                height: `${height}px`,
                overflow: "hidden", // Prevent overflow
            }}
        >
            <canvas ref={canvasRef} />
        </div>
    );
};
 
export default WordCloudDisplay;